import type { LapEntry, AmendedSessionAnalysis } from '@/types/telemetry';
import { formatTime, formatDelta } from '@/lib/format';
import { useRouter } from '@/context/RouterContext';
import { useI18n } from '@/context/I18nContext';

interface TheoreticalLapCardProps {
  sessionId: string;
  laps: LapEntry[];
  allTimeBestLapMs: number;
  allTimeBestSector: Record<string, number>;
  trackDisplayName: string;
  bestSectorsMs?: number[];
  amendedAnalysis?: AmendedSessionAnalysis;
  sessionAnalysisStatus?: 'completed' | 'pending' | string;
}

const SECTOR_KEYS = ['sector1Ms', 'sector2Ms', 'sector3Ms', 'sector4Ms', 'sector5Ms', 'sector6Ms'];

export function TheoreticalLapCard({
  sessionId,
  laps,
  allTimeBestLapMs,
  allTimeBestSector,
  trackDisplayName,
  bestSectorsMs,
  amendedAnalysis,
}: TheoreticalLapCardProps) {
  const { navigate } = useRouter();
  const { t } = useI18n();

  if (!laps || laps.length === 0) return null;

  // Session best per sector
  const sessionBestSector: Record<string, number> = {};
  if (bestSectorsMs && bestSectorsMs.length > 0) {
    SECTOR_KEYS.forEach((k, i) => {
      if (bestSectorsMs[i] && bestSectorsMs[i] > 0) {
        sessionBestSector[k] = bestSectorsMs[i];
      }
    });
  }
  if (amendedAnalysis?.theoreticalSectors && amendedAnalysis.theoreticalSectors.length > 0) {
    for (const ts of amendedAnalysis.theoreticalSectors) {
      const k = SECTOR_KEYS[ts.sectorNumber - 1];
      if (k && !sessionBestSector[k] && ts.bestDurationMs > 0) {
        sessionBestSector[k] = ts.bestDurationMs;
      }
    }
  }
  for (const lapEntry of laps) {
    const lap = lapEntry.lap;
    if (lap.lapTimeMs <= 0) continue;
    for (const k of SECTOR_KEYS) {
      const val = (lap as any)[k] as number;
      if (val > 0 && (!sessionBestSector[k] || val < sessionBestSector[k])) {
        sessionBestSector[k] = val;
      }
    }
  }

  // Determine max sector count
  let maxSectorCount = 0;
  for (const lapEntry of laps) {
    const lap = lapEntry.lap;
    for (let i = SECTOR_KEYS.length - 1; i >= maxSectorCount; i--) {
      if ((lap as any)[SECTOR_KEYS[i]] > 0) {
        maxSectorCount = i + 1;
        break;
      }
    }
  }
  for (let i = SECTOR_KEYS.length - 1; i >= maxSectorCount; i--) {
    if (sessionBestSector[SECTOR_KEYS[i]] > 0) {
      maxSectorCount = i + 1;
      break;
    }
  }

  const bestActualLapTimeMs = laps.reduce((min, l) => {
    const ms = l.lap.lapTimeMs;
    return ms > 0 && (min === 0 || ms < min) ? ms : min;
  }, 0);

  let theoLapTimeMs = 0;
  if (maxSectorCount > 0) {
    const activeKeys = SECTOR_KEYS.slice(0, maxSectorCount);
    if (activeKeys.every(k => sessionBestSector[k] > 0)) {
      theoLapTimeMs = activeKeys.reduce((sum, k) => sum + sessionBestSector[k], 0);
    }
  }
  if (theoLapTimeMs <= 0 && amendedAnalysis?.theoreticalBestTimeMs && amendedAnalysis.theoreticalBestTimeMs > 0) {
    theoLapTimeMs = amendedAnalysis.theoreticalBestTimeMs;
  }
  if (theoLapTimeMs <= 0) {
    theoLapTimeMs = bestActualLapTimeMs;
  }

  const theoDeltaMs = theoLapTimeMs > 0 && bestActualLapTimeMs > 0
    ? theoLapTimeMs - bestActualLapTimeMs
    : 0;
  const isTheoAllTimeBest = allTimeBestLapMs > 0 && theoLapTimeMs > 0 && theoLapTimeMs <= allTimeBestLapMs;
  const theoLapTimeCls = isTheoAllTimeBest ? 'td-purple' : 'td-green';
  const theoDeltaClass = theoDeltaMs > 0 ? 'text-red' : 'text-green';

  const handleOpenTheoLap = () => {
    const qp = new URLSearchParams();
    qp.set('session', sessionId);
    navigate(`#/analysis/sector/${encodeURIComponent(trackDisplayName)}?${qp.toString()}`);
  };

  return (
    <div className="card p-0 overflow-hidden mb-4">
      <div className="flex items-center justify-between px-4 pt-3.5 sm:px-5 sm:pt-4 pb-3 border-b border-border-subtle">
        <span className="text-xs font-semibold uppercase tracking-widest text-text-secondary">
          {t('lapTable.theoLap')}
        </span>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm border-collapse">
          <thead>
            <tr className="divider">
              <th className="text-left py-2 px-4 text-[0.7rem] font-semibold uppercase tracking-wider text-text-muted">{t('lapTable.lapTime')}</th>
              <th className="text-right py-2 px-4 text-[0.7rem] font-semibold uppercase tracking-wider text-text-muted">{t('lapTable.delta')}</th>
              {Array.from({ length: maxSectorCount }, (_, i) => (
                <th key={i} className="text-right py-2 px-4 text-[0.7rem] font-semibold uppercase tracking-wider text-text-muted">S{i + 1}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            <tr
              onClick={handleOpenTheoLap}
              className="hover:bg-bg-hover cursor-pointer transition-colors"
            >
              <td className={`py-2.5 px-4 mono font-semibold text-left ${theoLapTimeCls}`}>
                {formatTime(theoLapTimeMs)}
              </td>
              <td className={`py-2.5 px-4 mono text-right ${theoDeltaClass}`}>
                {theoLapTimeMs > 0 && bestActualLapTimeMs > 0 ? formatDelta(theoDeltaMs) : '—'}
              </td>
              {SECTOR_KEYS.slice(0, maxSectorCount).map(k => {
                const val = sessionBestSector[k];
                if (!val || val <= 0) return <td key={k} className="py-2.5 px-4 mono text-right text-text-muted">—</td>;
                const isAtBest = allTimeBestSector[k] > 0 && val <= allTimeBestSector[k];
                const cls = isAtBest ? 'td-purple' : 'td-green';
                return (
                  <td key={k} className={`py-2.5 px-4 mono text-right ${cls}`}>
                    {formatTime(val)}
                  </td>
                );
              })}
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}
